import { uuidv7 } from "./uuidv7.ts";

/**
 * `contracts/client-api.md` § Idempotency keys, FR-015-141, `research.md` W-08: a UUIDv7
 * generated when a mutation is created rather than when it is sent, carried through every
 * retry and through an offline queue period, never derived from the body. A 422
 * `idempotency_mismatch` can only be a client bug: the mutation is dropped and logged with
 * its request id rather than retried.
 */
export type IdempotencyKey = string & { readonly __brand: "IdempotencyKey" };

export function createIdempotencyKey(): IdempotencyKey {
  return uuidv7() as IdempotencyKey;
}

export const IDEMPOTENCY_MISMATCH = "idempotency_mismatch";

export const MUTATING_METHODS: ReadonlySet<string> = new Set(["POST", "PATCH", "PUT", "DELETE"]);

export function isMutation(method: string): boolean {
  return MUTATING_METHODS.has(method.toUpperCase());
}

/**
 * 001 `contracts/http-api.md` § Idempotency: the anonymous mutations that accept no
 * `Idempotency-Key` and answer 400 when one is sent, because nothing exists to key a
 * repeat to, the two first-run setup posts of FR-154 (2026-09-28), whose complete mints a
 * session, and the Microsoft Teams tab's sign-in (022, 2026-10-02), which exchanges the token
 * Teams issued for a session as `/auth/sign-in` exchanges a password for one. Every other
 * mutation, including the credential-minting routes, takes one.
 */
const KEYLESS_PATHS: ReadonlySet<string> = new Set(["/auth/sign-up", "/auth/sign-in", "/auth/teams/sign-in", "/auth/magic-code/request", "/auth/magic-code/verify", "/auth/password/reset/request", "/setup", "/setup/code"]);

/**
 * `POST /invites/{token}/join` (001 FR-012, 2026-09-30) and `POST /portal-invites/{token}/join`
 * (028 FR-028-012), keyless for the reason the code's verify is: the answer is a session cookie.
 */
const KEYLESS_INVITE_JOIN = /^\/(?:portal-)?invites\/[^/]+\/join$/;

export function acceptsIdempotencyKey(path: string): boolean {
  const relative = path.replace(/^\/api\/v1(?=\/)/, "").split("?")[0] ?? "";
  return !KEYLESS_PATHS.has(relative) && !KEYLESS_INVITE_JOIN.test(relative);
}

/** What the transport does with an `idempotency_mismatch`: drop, log the request id, never retry. */
export function reportIdempotencyMismatch(requestId: string, key: string, log: (message: string) => void = console.error): void {
  log(`idempotency_mismatch: key ${key} was reused with different data (request ${requestId}); the mutation is dropped`);
}
